import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "@/App";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useTheme } from "@/contexts/ThemeContext";
import DashboardLayout from "../components/DashboardLayout";
import { Bot, TrendingUp, TrendingDown, XCircle, Gem, DollarSign, Zap, Lightbulb, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PRESETS = [
  { label: "Hoy", key: "today" },
  { label: "Ayer", key: "yesterday" },
  { label: "7 días", key: "7d" },
  { label: "15 días", key: "15d" },
  { label: "30 días", key: "30d" },
  { label: "90 días", key: "90d" },
];

function chileDate(d) { const c = new Date(d); c.setHours(c.getHours() - 4); return c.toISOString().slice(0,10); }
function getDateRange(key) {
  const to = new Date();
  const from = new Date();
  if (key === "today") { return { from: chileDate(to), to: chileDate(to) }; }
  if (key === "yesterday") { const y = new Date(to); y.setDate(y.getDate()-1); return { from: chileDate(y), to: chileDate(y) }; }
  const days = { "7d":7, "15d":15, "30d":30, "90d":90 }[key] || 30;
  from.setDate(from.getDate() - days);
  return { from: chileDate(from), to: chileDate(to) };
}

export default function GoogleAdsAnalysis() {
  const { token } = useContext(AuthContext);
  const { clientId } = useWorkspace();
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [selectedKey, setSelectedKey] = useState("30d");
  const [error, setError] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [kwPage, setKwPage] = useState(1);
  const KW_PER_PAGE = 10;

  useEffect(() => { if (clientId) fetchData(); }, [clientId, selectedKey]);

  const fetchData = async () => {
    setLoading(true); setError(null); setAnalysis(null); setData(null); setKwPage(1);
    try {
      const { from, to } = getDateRange(selectedKey);
      const r = await axios.get(`${API}/google-ads/campaign-analysis`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { client_id: clientId, date_from: from, date_to: to }
      });
      setData(r.data);
      fetchKeywords();
    } catch (e) { setError("Error al cargar campañas."); }
    finally { setLoading(false); }
  };

  const fetchKeywords = async () => {
    setLoadingKeywords(true);
    try {
      const { from, to } = getDateRange(selectedKey);
      const r = await axios.get(`${API}/google-ads/keyword-intelligence`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { client_id: clientId, date_from: from, date_to: to }
      });
      setKeywords(r.data.keywords || []);
    } catch (e) { setKeywords([]); }
    finally { setLoadingKeywords(false); }
  };

  const runAnalysis = async () => {
    if (!data?.campaigns?.length) return;
    setAnalyzing(true); setAnalysis(null);
    try {
      const { from, to } = getDateRange(selectedKey);
      const r = await axios.get(`${API}/google-ads/keyword-intelligence`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { client_id: clientId, date_from: from, date_to: to }
      });
      setAnalysis(r.data.analysis);
    } catch (e) { setError("Error al generar análisis IA."); }
    finally { setAnalyzing(false); }
  };

  const fmt = (n) => typeof n === "number" ? n.toLocaleString("es-CL", { maximumFractionDigits: 0 }) : (n || "0");
  const fmtDec = (n) => typeof n === "number" ? n.toLocaleString("es-CL", { maximumFractionDigits: 2 }) : (n || "0");

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Google Ads IA</h1>
            <p className={`text-sm ${theme.textSecondary} mt-1`}>Detectamos exactamente dónde se gana y se pierde dinero</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {PRESETS.map(p => (
              <button key={p.key} onClick={() => setSelectedKey(p.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedKey === p.key ? "bg-blue-600 text-white" : `${theme.glassCard} ${theme.textSecondary}`}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {!clientId && (
          <div className={`${theme.glassCard} rounded-xl p-8 text-center`}>
            <p className={theme.textSecondary}>Selecciona un cliente para ver el análisis.</p>
          </div>
        )}

        {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4"><p className="text-red-600 text-sm">{error}</p></div>}

        {loading && (
          <div className={`${theme.glassCard} rounded-xl p-8 text-center`}>
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
            <p className={theme.textSecondary}>Cargando datos...</p>
          </div>
        )}

        {!loading && data?.campaigns?.length > 0 && (
          <>
            <div className={`${theme.glassCard} rounded-xl mb-6 overflow-hidden`}>
              <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
                <div>
                  <h2 className={`font-semibold ${theme.text}`}>Campañas Google Ads</h2>
                  <p className={`text-xs ${theme.textSecondary}`}>{data.date_from} → {data.date_to} · {data.campaigns.length} campañas</p>
                </div>
                <button onClick={runAnalysis} disabled={analyzing}
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-all disabled:opacity-50">
                  <Bot className="w-4 h-4" />
                  {analyzing ? "Analizando..." : "Analizar con IA"}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`${theme.textSecondary} text-xs uppercase bg-gray-50 dark:bg-gray-800/50`}>
                      <th className="text-left px-4 py-3">Campaña</th>
                      <th className="text-right px-4 py-3">Gasto</th>
                      <th className="text-right px-4 py-3">Clics</th>
                      <th className="text-right px-4 py-3">Impresiones</th>
                      <th className="text-right px-4 py-3">CTR</th>
                      <th className="text-right px-4 py-3">CPC</th>
                      <th className="text-right px-4 py-3">Conversiones</th>
                      <th className="text-right px-4 py-3">CPA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.campaigns.map((c, i) => (
                      <tr key={i} className="border-t border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className={`px-4 py-3 font-medium ${theme.text}`}>{c.campaign_name}</td>
                        <td className={`px-4 py-3 text-right ${theme.text}`}>${fmt(c.spent)}</td>
                        <td className={`px-4 py-3 text-right ${theme.textSecondary}`}>{fmt(c.clicks)}</td>
                        <td className={`px-4 py-3 text-right ${theme.textSecondary}`}>{fmt(c.impressions)}</td>
                        <td className={`px-4 py-3 text-right font-medium ${c.ctr > 5 ? "text-green-600" : c.ctr < 2 ? "text-red-500" : "text-yellow-600"}`}>{fmtDec(c.ctr)}%</td>
                        <td className={`px-4 py-3 text-right ${theme.textSecondary}`}>${fmtDec(c.cpc)}</td>
                        <td className={`px-4 py-3 text-right ${theme.text}`}>{fmtDec(c.conversions)}</td>
                        <td className={`px-4 py-3 text-right font-medium ${c.cpa < 200 ? "text-green-600" : c.cpa > 800 ? "text-red-500" : "text-yellow-600"}`}>${fmt(c.cpa)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* KPIs */}
            {data && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: "Gasto total", value: `$${fmt(data.campaigns.reduce((a,c)=>a+c.spent,0))}`, color: "text-gray-700 dark:text-gray-200" },
                  { label: "Conversiones", value: fmtDec(data.campaigns.reduce((a,c)=>a+c.conversions,0)), color: "text-green-600" },
                  { label: "Gasto ineficiente", value: analysis ? `$${fmt(analysis.total_waste)}` : "—", color: "text-red-500" },
                  { label: "Oportunidad estimada", value: analysis ? `$${fmt(analysis.growth_opportunity)}` : "—", color: "text-indigo-600" },
                ].map((k, i) => (
                  <div key={i} className={`${theme.glassCard} rounded-xl p-4`}>
                    <p className={`text-xs ${theme.textSecondary} mb-1`}>{k.label}</p>
                    <p className={`text-xl font-bold ${k.color}`}>{k.value}</p>
                  </div>
                ))}
              </div>
            )}

            {analyzing && (
              <div className={`${theme.glassCard} rounded-xl p-8 text-center mb-4`}>
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full" />
                  <Bot className="w-6 h-6 text-indigo-500" />
                </div>
                <p className={`font-medium ${theme.text}`}>Analizando con IA...</p>
                <p className={`text-sm ${theme.textSecondary} mt-1`}>Identificando money makers, wasters y oportunidades</p>
              </div>
            )}

            {analysis && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-5 text-white">
                  <div className="flex items-center gap-2 mb-2"><Bot className="w-5 h-5" /><span className="font-semibold">Resumen ejecutivo</span></div>
                  <p className="text-blue-50 text-sm leading-relaxed">{analysis.executive_summary || analysis.summary}</p>
                  <div className="flex gap-3 mt-3 flex-wrap">
                    {analysis.total_waste > 0 && <div className="bg-white/20 rounded-lg px-3 py-1.5 text-sm font-semibold">💸 Gasto ineficiente: ${fmt(analysis.total_waste)}</div>}
                    {analysis.growth_opportunity > 0 && <div className="bg-white/20 rounded-lg px-3 py-1.5 text-sm font-semibold">🚀 Oportunidad: ${fmt(analysis.growth_opportunity)}</div>}
                  </div>
                  {analysis.pareto_names?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2 items-center">
                      <span className="text-xs text-blue-200">🎯 Pareto 80%:</span>
                      {analysis.pareto_names.map((n, i) => <span key={i} className="text-xs bg-white/20 rounded-full px-2 py-0.5">{n}</span>)}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {analysis.money_makers?.length > 0 && (
                    <div className={`${theme.glassCard} rounded-xl p-5`}>
                      <div className="flex items-center gap-2 mb-4"><DollarSign className="w-5 h-5 text-green-500" /><h3 className={`font-semibold ${theme.text}`}>🔥 Money Makers</h3><span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full ml-auto">ESCALAR</span></div>
                      <div className="space-y-3">
                        {analysis.money_makers.map((k, i) => (
                          <div key={i} className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                            <div className="flex justify-between mb-1"><span className={`font-medium text-sm ${theme.text}`}>{k.name}</span><span className="text-xs text-green-600 font-semibold">${fmt(k.spent)}</span></div>
                            <p className="text-xs text-green-700 dark:text-green-400 mb-1">{k.reason}</p>
                            <div className="flex gap-3 text-xs text-green-600">
                              {k.conversions > 0 && <span>Conv: {fmtDec(k.conversions)}</span>}
                              {k.cpa > 0 && <span>CPA: ${fmt(k.cpa)}</span>}
                              {k.ctr > 0 && <span>CTR: {fmtDec(k.ctr)}%</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {analysis.money_wasters?.length > 0 && (
                    <div className={`${theme.glassCard} rounded-xl p-5`}>
                      <div className="flex items-center gap-2 mb-4"><XCircle className="w-5 h-5 text-red-500" /><h3 className={`font-semibold ${theme.text}`}>🚨 Money Wasters</h3><span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full ml-auto">PAUSAR</span></div>
                      <div className="space-y-3">
                        {analysis.money_wasters.map((k, i) => (
                          <div key={i} className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                            <div className="flex justify-between mb-1"><span className={`font-medium text-sm ${theme.text}`}>{k.name}</span><span className="text-xs text-red-600 font-semibold">-${fmt(k.waste || k.spent)}</span></div>
                            <p className="text-xs text-red-600 dark:text-red-400 mb-1">{k.action}</p>
                            <div className="flex gap-3 text-xs text-red-500"><span>Gasto: ${fmt(k.spent)}</span><span>Conv: {fmtDec(k.conversions)}</span></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {analysis.hidden_gems?.length > 0 && (
                    <div className={`${theme.glassCard} rounded-xl p-5`}>
                      <div className="flex items-center gap-2 mb-4"><Gem className="w-5 h-5 text-purple-500" /><h3 className={`font-semibold ${theme.text}`}>💎 Hidden Gems</h3></div>
                      <div className="space-y-3">
                        {analysis.hidden_gems.map((k, i) => (
                          <div key={i} className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                            <span className={`font-medium text-sm ${theme.text} block mb-1`}>{k.name}</span>
                            <p className="text-xs text-purple-700 dark:text-purple-400">{k.action}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {analysis.priority_actions?.length > 0 && (
                    <div className={`${theme.glassCard} rounded-xl p-5`}>
                      <div className="flex items-center gap-2 mb-4"><Zap className="w-5 h-5 text-indigo-500" /><h3 className={`font-semibold ${theme.text}`}>🚀 Acciones prioritarias</h3></div>
                      <div className="space-y-2">
                        {analysis.priority_actions.map((a, i) => (
                          <div key={i} className="flex items-start gap-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3">
                            <span className="w-5 h-5 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{i+1}</span>
                            <p className="text-sm text-indigo-800 dark:text-indigo-300">{a}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {(analysis.trending?.length > 0 || analysis.declining?.length > 0) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {analysis.trending?.length > 0 && (
                      <div className={`${theme.glassCard} rounded-xl p-5`}>
                        <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-5 h-5 text-blue-500" /><h3 className={`font-semibold ${theme.text}`}>📈 Trending</h3></div>
                        <div className="space-y-2">
                          {analysis.trending.map((k, i) => (
                            <div key={i} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                              <span className={`font-medium text-sm ${theme.text} block mb-1`}>{k.name}</span>
                              <p className="text-xs text-blue-700 dark:text-blue-400">{k.reason}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {analysis.declining?.length > 0 && (
                      <div className={`${theme.glassCard} rounded-xl p-5`}>
                        <div className="flex items-center gap-2 mb-4"><TrendingDown className="w-5 h-5 text-orange-500" /><h3 className={`font-semibold ${theme.text}`}>📉 Declining</h3></div>
                        <div className="space-y-2">
                          {analysis.declining.map((k, i) => (
                            <div key={i} className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
                              <span className={`font-medium text-sm ${theme.text} block mb-1`}>{k.name}</span>
                              <p className="text-xs text-orange-700 dark:text-orange-400">{k.reason}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Tabla Keywords por Ranking */}
            <div className={`${theme.glassCard} rounded-xl overflow-hidden mt-4`}>
              <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <h3 className={`font-semibold ${theme.text}`}>🔑 Palabras Clave por Ranking</h3>
                  <p className={`text-xs ${theme.textSecondary}`}>Ordenadas por gasto · {keywords.length} keywords</p>
                </div>
                {loadingKeywords && <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full" />}
              </div>
              {keywords.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`${theme.textSecondary} text-xs uppercase bg-gray-50 dark:bg-gray-800/50`}>
                        <th className="text-left px-4 py-3">#</th>
                        <th className="text-left px-4 py-3">Keyword</th>
                        <th className="text-right px-4 py-3">Clics</th>
                        <th className="text-right px-4 py-3">Impresiones</th>
                        <th className="text-right px-4 py-3">CTR</th>
                        <th className="text-right px-4 py-3">CPC</th>
                        <th className="text-right px-4 py-3">Conv.</th>
                        <th className="text-right px-4 py-3">CPA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keywords.slice((kwPage-1)*KW_PER_PAGE, kwPage*KW_PER_PAGE).map((k, i) => (
                        <tr key={i} className="border-t border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          <td className={`px-4 py-3 text-xs font-bold ${theme.textSecondary}`}>{i + 1}</td>
                          <td className={`px-4 py-3 font-medium ${theme.text} max-w-xs`}>
                            <div>{k.keyword}</div>
                            {k.match_type && <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded px-1.5 py-0.5 mt-0.5 inline-block">{k.match_type}</span>}
                          </td>
                          <td className={`px-4 py-3 text-right ${theme.textSecondary}`}>{fmt(k.clicks)}</td>
                          <td className={`px-4 py-3 text-right ${theme.textSecondary}`}>{fmt(k.impressions)}</td>
                          <td className={`px-4 py-3 text-right font-medium ${k.ctr > 5 ? "text-green-600" : k.ctr < 2 ? "text-red-500" : "text-yellow-600"}`}>{fmtDec(k.ctr)}%</td>
                          <td className={`px-4 py-3 text-right ${theme.textSecondary}`}>${fmtDec(k.cpc)}</td>
                          <td className={`px-4 py-3 text-right ${theme.text}`}>{fmtDec(k.conversions)}</td>
                          <td className={`px-4 py-3 text-right font-medium ${k.cpa > 0 && k.cpa < 200 ? "text-green-600" : k.cpa > 800 ? "text-red-500" : k.cpa > 0 ? "text-yellow-600" : theme.textSecondary}`}>{k.cpa > 0 ? `$${fmt(k.cpa)}` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Paginación */}
                  {keywords.length > KW_PER_PAGE && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                      <p className={`text-xs ${theme.textSecondary}`}>
                        Mostrando {(kwPage-1)*KW_PER_PAGE+1}–{Math.min(kwPage*KW_PER_PAGE, keywords.length)} de {keywords.length} keywords
                      </p>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setKwPage(p => Math.max(1, p-1))} disabled={kwPage === 1}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${kwPage === 1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700'} ${theme.textSecondary}`}>
                          ← Anterior
                        </button>
                        {Array.from({length: Math.ceil(keywords.length/KW_PER_PAGE)}, (_, i) => i+1)
                          .filter(p => p === 1 || p === Math.ceil(keywords.length/KW_PER_PAGE) || Math.abs(p - kwPage) <= 2)
                          .map((p, idx, arr) => (
                            <span key={p}>
                              {idx > 0 && arr[idx-1] !== p-1 && <span className={`px-1 ${theme.textSecondary}`}>…</span>}
                              <button onClick={() => setKwPage(p)}
                                className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${kwPage === p ? 'bg-blue-600 text-white' : `${theme.textSecondary} hover:bg-gray-100 dark:hover:bg-gray-700`}`}>
                                {p}
                              </button>
                            </span>
                          ))
                        }
                        <button onClick={() => setKwPage(p => Math.min(Math.ceil(keywords.length/KW_PER_PAGE), p+1))} disabled={kwPage === Math.ceil(keywords.length/KW_PER_PAGE)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${kwPage === Math.ceil(keywords.length/KW_PER_PAGE) ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700'} ${theme.textSecondary}`}>
                          Siguiente →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                !loadingKeywords && (
                  <div className="p-8 text-center">
                    <p className={`text-sm ${theme.textSecondary}`}>No hay datos de keywords disponibles para este período.</p>
                  </div>
                )
              )}
            </div>

            {/* Tabla datos completos */}
            <div className={`${theme.glassCard} rounded-xl overflow-hidden mt-4`}>
              <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className={`font-semibold ${theme.text}`}>Datos completos</h3>
                <p className={`text-xs ${theme.textSecondary}`}>{data.date_from} → {data.date_to} · {data.campaigns.length} campañas</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`${theme.textSecondary} text-xs uppercase bg-gray-50 dark:bg-gray-800/50`}>
                      <th className="text-left px-4 py-3">Campaña</th>
                      <th className="text-right px-4 py-3">Gasto</th>
                      <th className="text-right px-4 py-3">Clics</th>
                      <th className="text-right px-4 py-3">CTR</th>
                      <th className="text-right px-4 py-3">Conv.</th>
                      <th className="text-right px-4 py-3">CPA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.campaigns.map((c, i) => (
                      <tr key={i} className="border-t border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className={`px-4 py-3 font-medium ${theme.text}`}>{c.campaign_name}</td>
                        <td className={`px-4 py-3 text-right ${theme.text}`}>${fmt(c.spent)}</td>
                        <td className={`px-4 py-3 text-right ${theme.textSecondary}`}>{fmt(c.clicks)}</td>
                        <td className={`px-4 py-3 text-right font-medium ${c.ctr > 5 ? "text-green-600" : c.ctr < 2 ? "text-red-500" : "text-yellow-600"}`}>{fmtDec(c.ctr)}%</td>
                        <td className={`px-4 py-3 text-right ${theme.text}`}>{fmtDec(c.conversions)}</td>
                        <td className={`px-4 py-3 text-right font-medium ${c.cpa < 200 ? "text-green-600" : c.cpa > 800 ? "text-red-500" : "text-yellow-600"}`}>${fmt(c.cpa)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
